import errorTypes from '../errors/errorTypes';
import { getCheckoutErrorInfo, requiresNewPaymentMethod } from './checkoutErrorCodes';

const GENERIC_MESSAGE = 'This order could not be submitted. Please review your order and try again.';

/**
 * An error raised when submitting an order fails in a way the shopper can act on.
 *
 * The API returns a `detail` string alongside its error code. For a declined card that detail is the
 * payment gateway's own reason, which is useful in logs and poor practice to display, so it is kept on
 * {@link CheckoutError#detail detail} while {@link CheckoutError#message message} stays generic.
 *
 * @property {string} type - "CHECKOUT" - used to determine which type of Collaterate error has been thrown.
 * @property {?string} code - The Collaterate error code, e.g. "1-106".
 * @property {string} message - A message safe to show a shopper.
 * @property {string} detail - The raw detail from the API. Log this; do not display it.
 * @property {?string} step - Which {@link Collaterate.Checkout.checkoutSteps checkout step} to return the shopper to. Null if unknown.
 * @property {boolean} requiresNewPaymentMethod - Whether the stored payment token is now spent and a fresh one must be collected before retrying.
 * @property {number} status - The HTTP status code.
 * @property {?string} id - The Collaterate error id, for finding this failure in logs.
 * @property {Object} cause - The underlying error this was built from.
 */

class CheckoutError {
    constructor (e = {}) {
        const info = getCheckoutErrorInfo(e.code);

        this.type = errorTypes.CHECKOUT;
        this.code = e.code || null;
        this.message = info ? info.message : GENERIC_MESSAGE;
        this.detail = e.message || '';
        this.step = info ? info.step : null;
        this.requiresNewPaymentMethod = requiresNewPaymentMethod(this.code);
        this.status = e.status;
        this.id = e.id || null;
        this.cause = e;

        Object.freeze(this);
    }
}

export default CheckoutError;
